// src/components/AIAnalyzer.tsx - FULLY FIXED
import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  Target, Users, MessageSquare, MousePointer, Sparkles, 
  TrendingUp, Check, X, AlertCircle, CreditCard, Loader
} from 'lucide-react'
import { 
  getUserPlanLimits, 
  getAvailableAIPackages,
  AIPackage 
} from '../config/planLimits'
import './AIAnalyzer.css'

interface SmartBrief {
  campaignGoal: string
  customGoal?: string
  targetAudience: string
  keyMessage: string
  desiredAction: string
  description: string
}

interface MLSuggestions {
  suggestedTrigger?: string; triggerReasoning?: string; triggerLift?: number;
  suggestedTone?: string; toneReasoning?: string; toneLift?: number;
  suggestedCTA?: string; ctaReasoning?: string; ctaLift?: number;
  predictedObjection?: string; objectionLikelihood?: number;
  suggestedReframe?: string; dataSource: string;
}

interface AIAnalyzerProps {
  brief: SmartBrief
  onBriefChange: (brief: SmartBrief) => void
  campaignData?: any
  onAnalyze?: () => void
  onApplySuggestions?: (suggestions: MLSuggestions) => void
  aiAnalysisUsed: number
  onUsageUpdate?: (newCount: number) => void
  onUpgrade?: () => void
}

export default function AIAnalyzer({
  brief, onBriefChange, campaignData, onAnalyze,
  onApplySuggestions, aiAnalysisUsed, onUsageUpdate, onUpgrade
}: AIAnalyzerProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  
  const [mlSuggestions, setMlSuggestions] = useState<MLSuggestions | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPricingModal, setShowPricingModal] = useState(false)
  const [purchasedCredits, setPurchasedCredits] = useState(0)
  const [localUsageCount, setLocalUsageCount] = useState(aiAnalysisUsed)
  const [usageLoading, setUsageLoading] = useState(true)
  
const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'
  const userPlan = (user as any)?.selectedPlan || (user as any)?.plan || 'starter'
  const userEmail = (user as any)?.email || ''
  const planLimits = getUserPlanLimits(userPlan)
  const availablePackages = getAvailableAIPackages(userPlan)
  
  const freeLimit = planLimits.maxAIAnalyses
  const remainingFree = Math.max(0, freeLimit - localUsageCount)
  const canAnalyze = remainingFree > 0 || purchasedCredits > 0

  const apiCall = useCallback(async <T,>(url: string, options: RequestInit = {}, timeout = 30000): Promise<{ success: boolean; data?: T; error?: string }> => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken')
      if (!token) return { success: false, error: 'Not authenticated' }
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)
      const response = await fetch(url, { ...options, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers }, signal: controller.signal })
      clearTimeout(timeoutId)
      if (!response.ok) {
        let msg = `Error: ${response.status}`
        try { const d = await response.json(); msg = d.message || d.error || msg } catch {}
        return { success: false, error: msg }
      }
      const data = await response.json()
      return { success: true, data }
    } catch (error: any) {
      return { success: false, error: error.message || 'Unknown error' }
    }
  }, [])

 // ✅ Fetch real usage from backend on mount
useEffect(() => {
  const fetchRealUsage = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/ai-analysis/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data?.data?.monthlyFreeUsed !== undefined) {
        setLocalUsageCount(data.data.monthlyFreeUsed);
      }
    } catch (err) { console.error('Fetch usage error:', err); }
  };
  fetchRealUsage();
}, []);

const analyzeBrief = useCallback(async () => {
  const validationError = !brief.targetAudience?.trim() ? 'Target audience required' 
    : !brief.keyMessage?.trim() ? 'Key message required' 
    : !brief.description?.trim() ? 'Description required' 
    : null;
  
  if (validationError) { 
    setError(validationError); 
    return; 
  }
  
  if (!canAnalyze) { 
    setShowPricingModal(true); 
    return; 
  }
  
  setAnalyzing(true); 
  setError(null);
  
  try {
    // ✅ FIXED: Added apiCall function
    const analysisResult = await apiCall(`${API_URL}/emailbrain/analyze-brief`, {
      method: 'POST',
      body: JSON.stringify({ 
        brief: { 
          campaignGoal: brief.campaignGoal, 
          targetAudience: brief.targetAudience, 
          keyMessage: brief.keyMessage, 
          desiredAction: brief.desiredAction, 
          description: brief.description 
        }, 
        nicheId: campaignData?.nicheId || 'SAAS_B2B_01' 
      })
    }, 30000);

    if (!analysisResult.success) { 
      setError(analysisResult.error); 
      setAnalyzing(false); 
      return; 
    }

    const responseData = analysisResult.data as any;
    
    // ✅ FIXED: Removed duplicate /api
    const consumeResult = await apiCall(`${API_URL}/ai-analysis/consume`, {
      method: 'POST', 
      body: JSON.stringify({ campaignId: campaignData?.id })
    }, 10000);
    
    // ✅ FIXED: Added missing closing brackets and logic
    if (consumeResult.success && consumeResult.data) {
      const newCount = localUsageCount + 1;
      setLocalUsageCount(newCount);
      onUsageUpdate?.(newCount);
      
      const data = consumeResult.data as any;
      if (data.source === 'purchased_credit') {
        setPurchasedCredits(prev => Math.max(0, prev - 1));
      }
    }

    const suggestions: MLSuggestions = {
      dataSource: responseData.dataSource || 'EmailBrain AI',
      suggestedTrigger: responseData.suggestedTrigger,
      suggestedCTA: responseData.suggestedCTA,
      predictedObjection: responseData.predictedObjection,
      triggerLift: responseData.triggerLift, 
      ctaLift: responseData.ctaLift,
      objectionLikelihood: responseData.objectionLikelihood,
      suggestedReframe: responseData.suggestedReframe
    };
    
    setMlSuggestions(suggestions);
    setShowSuggestions(true);
    onAnalyze?.();
    
  } catch (error: any) {
    setError(error.message || 'Analysis failed');
  } finally {
    setAnalyzing(false);
  }
}, [brief, campaignData, canAnalyze, localUsageCount, onUsageUpdate, onAnalyze, apiCall, API_URL]);




  const handlePurchasePackage = (pkg: AIPackage) => {
    navigate('/payment', { state: { type: 'ai-analysis', package: pkg, email: userEmail, plan: userPlan } })
    setShowPricingModal(false)
  }
  
  const handleUpgrade = () => {
    if (onUpgrade) onUpgrade()
    else navigate('/plan-selection', { state: { from: 'ai-analyzer', currentPlan: userPlan, email: userEmail } })
    setShowPricingModal(false)
  }

  const handleApplySuggestions = () => {
    if (mlSuggestions?.suggestedTrigger) onBriefChange({ ...brief, keyMessage: mlSuggestions.suggestedTrigger })
    if (mlSuggestions?.suggestedCTA) onBriefChange({ ...brief, desiredAction: mlSuggestions.suggestedCTA })
    onApplySuggestions?.(mlSuggestions!)
    setShowSuggestions(false)
  }

  return (
    <div className="ai-analyzer">
      <div className="description-section">
        <label className="section-label"><MessageSquare size={16} /> Describe what you want</label>
        <textarea value={brief.description || ''} onChange={(e) => onBriefChange({ ...brief, description: e.target.value })} placeholder="Describe your campaign goals..." rows={4} className="description-textarea" maxLength={2000} />
        <div className="char-count">{(brief.description || '').length}/2000</div>
      </div>
      
      <div className="brief-section">
        <div className="brief-header"><div className="brief-icon-wrapper"><Target size={20} /></div><div><h3 className="brief-title">Campaign Brief</h3><p className="brief-description">4 key inputs for AI analysis</p></div></div>
        
        <div className="brief-grid">
          <div className="brief-field">
            <label><Target size={14} /> Campaign Goal</label>
            <select value={brief.campaignGoal} onChange={(e) => onBriefChange({ ...brief, campaignGoal: e.target.value, customGoal: e.target.value === 'others' ? '' : undefined })}>
              <option value="">Select...</option><option value="lead_generation">📈 Generate Leads</option><option value="sales_conversion">💰 Drive Sales</option><option value="brand_awareness">📢 Build Awareness</option><option value="customer_retention">🔄 Retain Customers</option><option value="others">✏️ Custom</option>
            </select>
          </div>
          <div className="brief-field"><label><Users size={14} /> Target Audience</label><input type="text" value={brief.targetAudience} onChange={(e) => onBriefChange({ ...brief, targetAudience: e.target.value })} placeholder="e.g., Marketing Directors" maxLength={200} /></div>
          <div className="brief-field"><label><MessageSquare size={14} /> Key Message</label><input type="text" value={brief.keyMessage} onChange={(e) => onBriefChange({ ...brief, keyMessage: e.target.value })} placeholder="e.g., Reduce campaign time by 75%" maxLength={200} /></div>
          <div className="brief-field"><label><MousePointer size={14} /> Desired Action</label><input type="text" value={brief.desiredAction} onChange={(e) => onBriefChange({ ...brief, desiredAction: e.target.value })} placeholder="e.g., Book a demo" maxLength={200} /></div>
        </div>
        
        {/* ✅ CREDIT TRACKER */}
        <div className="usage-info">
          <div className="usage-bar"><div className="usage-progress" style={{ width: `${freeLimit > 0 ? Math.min(100, (localUsageCount / freeLimit) * 100) : 0}%` }} /></div>
          <p className="usage-text">
            <strong>{remainingFree} of {freeLimit}</strong> free analyses remaining
            {purchasedCredits > 0 && <span className="purchased-credits"> • {purchasedCredits} purchased</span>}
          </p>
        </div>
        
        <button onClick={analyzeBrief} disabled={analyzing} className="analyze-btn">
          {analyzing ? <><Loader size={16} className="spinner" /> Analyzing...</> : <><Sparkles size={16} /> {canAnalyze ? 'Analyze with AI' : 'Purchase Analysis'}{!canAnalyze && <CreditCard size={14} />}</>}
        </button>
        
        {error && <div className="error-message"><AlertCircle size={14} /><p>{error}</p></div>}
      </div>
      
      {showPricingModal && (
        <div className="pricing-modal-overlay" onClick={() => setShowPricingModal(false)}>
          <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>Need More AI Analysis?</h3><button onClick={() => setShowPricingModal(false)}><X size={20} /></button></div>
            <p className="modal-description">You've used all {freeLimit} free analyses. Purchase more or upgrade.</p>
            <div className="packages-grid">
              {availablePackages.map((pkg) => (
                <div key={pkg.id} className={`package-card ${pkg.popular ? 'popular' : ''}`}>
                  {pkg.popular && <span className="popular-badge">Popular</span>}
                  <h4>{pkg.name}</h4><p>{pkg.description}</p>
                  <div className="package-price">${pkg.price}<span className="price-per">one-time</span></div>
                  <button onClick={() => handlePurchasePackage(pkg)} className="purchase-btn">Purchase</button>
                </div>
              ))}
            </div>
            <div className="modal-footer"><button onClick={handleUpgrade} className="upgrade-plan-btn">Upgrade Plan →</button></div>
          </div>
        </div>
      )}
      
      {showSuggestions && mlSuggestions && (
        <div className="suggestions-panel">
          <div className="suggestions-header"><span>🤖</span><h3>AI Suggestions</h3><button onClick={() => setShowSuggestions(false)}><X size={16} /></button></div>
          <div className="suggestions-list">
            {mlSuggestions.suggestedTrigger && <div className="suggestion-card"><div className="suggestion-header"><Target size={16} /><h4>Trigger</h4></div><p>{mlSuggestions.suggestedTrigger}</p></div>}
            {mlSuggestions.suggestedCTA && <div className="suggestion-card"><div className="suggestion-header"><MousePointer size={16} /><h4>CTA</h4></div><p>{mlSuggestions.suggestedCTA}</p></div>}
            {mlSuggestions.predictedObjection && <div className="objection-card"><h4>⚠️ Objection</h4><p>"{mlSuggestions.predictedObjection}"</p>{mlSuggestions.suggestedReframe && <p><strong>Response:</strong> {mlSuggestions.suggestedReframe}</p>}</div>}
          </div>
          <div className="suggestions-footer"><button onClick={handleApplySuggestions} className="apply-all-btn"><Check size={16} /> Apply</button><button onClick={() => setShowSuggestions(false)} className="dismiss-btn">Keep Original</button></div>
        </div>
      )}
    </div>
  )
}